import { useState } from 'react'
import { Star, UserMinus, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useCommitteeAppointments,
  useCommitteeHistory,
  useEndAppointment,
} from '@/hooks/use-committee'
import { COMMITTEE_POSITION_LABELS, type CommitteeAppointment } from '@riverpark/types'

function formatDate(ts: number) {
  return new Date(ts * 1000).toLocaleDateString('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

interface ProfileAppointmentPanelProps {
  memberId: string
  memberName: string
  currentAppointment: CommitteeAppointment | null | undefined
}

export function ProfileAppointmentPanel({
  memberId,
  memberName,
  currentAppointment,
}: ProfileAppointmentPanelProps) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const { data } = useCommitteeAppointments()
  const { data: historyData } = useCommitteeHistory()
  const endAppointment = useEndAppointment()

  const pastAppointments = (historyData?.data ?? []).filter(
    (a) => a.memberId === memberId && a.endedAt !== null,
  )

  // Prefer live data from the appointments query; fall back to member detail
  const appointments = data?.data ?? []
  const liveAppointment =
    appointments.find((a) => a.memberId === memberId) ?? currentAppointment ?? null

  async function handleEnd(appointmentId: string) {
    if (!confirm(`End ${memberName}'s current committee position?`)) return
    await endAppointment.mutateAsync(appointmentId)
  }

  return (
    <div className="space-y-4">
      {/* Current appointment */}
      {liveAppointment ? (
        <div className="flex items-start justify-between gap-4 rounded border border-border-2 bg-cream-2/40 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Star size={15} className="shrink-0 text-amber-500" strokeWidth={1.75} />
            <div>
              <p className="font-sans text-sm font-medium text-ink">
                {COMMITTEE_POSITION_LABELS[liveAppointment.position]}
              </p>
              <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                Since {formatDate(liveAppointment.startedAt)}
                {liveAppointment.appointedByName &&
                  ` · Appointed by ${liveAppointment.appointedByName}`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-status-overdue-text hover:bg-status-overdue-text/10"
            onClick={() => void handleEnd(liveAppointment.id)}
            disabled={endAppointment.isPending}
          >
            <UserMinus size={14} strokeWidth={1.75} className="mr-1.5" />
            End term
          </Button>
        </div>
      ) : (
        <p className="font-sans text-sm text-muted-foreground">
          No committee position assigned. Use the Committee tab to appoint this member.
        </p>
      )}

      {/* Past appointments for this member */}
      {pastAppointments.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex w-full items-center justify-between font-sans text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-ink"
          >
            <span>Past positions ({pastAppointments.length})</span>
            {historyOpen ? (
              <ChevronUp size={13} strokeWidth={1.75} />
            ) : (
              <ChevronDown size={13} strokeWidth={1.75} />
            )}
          </button>

          {historyOpen && (
            <div className="divide-y divide-border-2 overflow-hidden rounded border border-border-2">
              {pastAppointments.map((appt) => (
                <div key={appt.id} className="px-4 py-3">
                  <p className="font-sans text-sm font-medium text-ink">
                    {COMMITTEE_POSITION_LABELS[appt.position]}
                  </p>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                    {formatDate(appt.startedAt)} → {appt.endedAt ? formatDate(appt.endedAt) : ''}
                    {appt.appointedByName && ` · Appointed by ${appt.appointedByName}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
